import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import './GratitudeTracker.css';
import { UserContext } from './UserContext';
import GratitudeWordCloud from "./components/WordCloud";

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

// ✅ Move gratitudeMessages outside the component to prevent unnecessary re-renders
const gratitudeMessages = [
  "Every entry adds to your joy.",
  "With every entry, joy grows.",
  "Gratitude transforms your mindset—one entry at a time.",
  "One more entry, one more reason to smile.",
  "A little gratitude goes a long way—keep adding.",
  "The habit of gratitude shapes a happier you—keep it up.",
  "Gratitude builds on itself—watch it grow.",
  "Each moment of gratitude shapes a brighter tomorrow.",
  "Your words shape your joy—keep expressing gratitude.",
  "One entry at a time, you're creating a life of appreciation.",
  "Keep the gratitude streak alive—your joy is waiting.",
  "Every grateful thought makes your heart a little lighter.",
  "The more you notice, the more joy you create.",
  "Be grateful. Be joyful. Be you.",
  "Gratitude turns the ordinary into something extraordinary.",
  "Another entry, another reason to feel good.",
  "Gratitude is a magnet for happiness—keep attracting it.",
  "Your gratitude cloud is growing beautifully—keep going.",
  "The more you acknowledge, the brighter your world becomes.",
  "Gratitude multiplies joy—you're on the right track.",
  "Each moment of appreciation shapes a better tomorrow.",
  "Your gratitude habit is making a difference.",
  "The simple act of gratitude changes everything.",
  "Small moments of gratitude add up to a life of joy.",
  "You're wiring your brain for happiness—one entry at a time.",
  "Every entry strengthens your gratitude muscle—keep exercising.",
  "Each entry is a step toward a more positive mindset.",
  "Gratitude is like sunshine for the soul—keep shining.",
  "Your words of gratitude create ripples of joy.",
  "Joy expands when you acknowledge the good in your life.",
  "Gratitude is contagious—spread some today.",
  "Your gratitude habit is shaping a brighter future.",
  "The more you appreciate, the richer your experience becomes.",
  "Your gratitude journey is unfolding beautifully.",
  "One grateful thought leads to another—let it flow!",
  "You're creating a gratitude practice for a lifetime of joy.",
  "Gratitude sharpens your focus on what truly matters.",
  "Each entry strengthens your connection to joy.",
  "Keep your gratitude flame burning bright.",
  "The smallest gratitude can bring the biggest joy.",
  "Your gratitude cloud is taking shape—keep adding to it.",
  "Gratitude is the key to unlocking joy in your life.",
  "One entry today, a lifetime of gratitude ahead!",
  "Every entry is a seed of joy—watch it grow!",
  "The best time for gratitude is always now.",
  "Your heart expands with every grateful thought.",
  "Gratitude rewires your brain for positivity.",
  "Thankfulness turns what you have into more than enough.",
  "The more you give thanks, the more joy you receive.",
  "Your gratitude journey is a beautiful work in progress.",
  "Gratitude is the music of your heart—keep the melody going.",
  "Your gratitude practice is a gift to your future self.",
  "Every entry is a step toward a more joyful you.",
  "Gratitude is the path to a more fulfilling life.",
  "The more you appreciate, the more joy you create.",
  "Gratitude is the foundation of a happy life.",
  "Every entry is a step toward a more positive mindset.",
  "Gratitude is the bridge to a more joyful life.",
  "Your gratitude practice is a gift to your soul.",
  "The more you notice, the more joy you find.",
];

const GratitudeTracker = () => {
  const { user } = useContext(UserContext);
  const [newEntries, setNewEntries] = useState(['', '', '', '', '']);
  const [message, setMessage] = useState('');
  const [randomMessage, setRandomMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingEntry, setEditingEntry] = useState(null);
  const logoPath = "/images/gratiSphere.svg";
  
  const [stats, setStats] = useState({
    allEntries: [],
    totalDaysRecorded: 0,
    currentStreak: 0,
    longestStreak: 0,
    startYear: '',
    startMonth: '',
    monthlyEntries: {},
    latestEntryDate: null,
  });

  // ✅ Now this useEffect won't trigger the ESLint warning
  useEffect(() => {
    setRandomMessage(gratitudeMessages[Math.floor(Math.random() * gratitudeMessages.length)]);
  }, []); // Runs only once on mount


/** ✅ Fetch Gratitude Data */
const fetchGratitudeData = useCallback(async () => {
  if (!user || !user.id || !user.token) return;

  try {
      const response = await axios.get(`${API_BASE_URL}/user/${user.id}/gratitude`, {
          headers: { Authorization: `Bearer ${user.token}` },
      });

      if (!response.data || Object.keys(response.data).length === 0) {
          setStats(prevStats => ({
              ...prevStats,
              allEntries: [],
              latestEntry: [],
              totalDaysRecorded: 0,
              currentStreak: 0,
              longestStreak: 0,
              startYear: new Date().getFullYear(),
              startMonth: new Date().toLocaleString('en-US', { month: 'long' }),
              latestEntryDate: null,
              monthlyEntries: {},
          }));
          return;
      }

      const { allEntries = [], latestEntryDate = null, totalDaysRecorded = 0, currentStreak = 0, longestStreak = 0, startYear = new Date().getFullYear(), startMonth = new Date().toLocaleString('en-US', { month: 'long' }), monthlyEntries = {} } = response.data;

      // ✅ Ensure `allEntries` contains correct structure
      const formattedAllEntries = allEntries.map(entry => ({
          _id: entry._id || new Date().getTime(),
          gratitudeSubject: entry.gratitudeSubject || "Untitled",
          gratitudeDetails: entry.gratitudeDetails || "",
          date: entry.date || new Date().toISOString(),
          formattedDate: new Date(entry.date || new Date()).toLocaleString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
          }),
      }));

      // ✅ Sort and extract latest 5 unique daily entries
      const sortedEntries = formattedAllEntries
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const latestEntries = sortedEntries.slice(0, 5);

      setStats(prevStats => ({
          ...prevStats,
          allEntries: sortedEntries,
          latestEntry: latestEntries, // ✅ Dynamically updated latest entries
          totalDaysRecorded,
          currentStreak,
          longestStreak,
          startYear,
          startMonth,
          latestEntryDate,
          monthlyEntries,
      }));

  } catch (error) {
      console.error("❌ Error fetching gratitude data:", error);
      setMessage('What are you grateful for? Add your first entry.');
      setStats(prevStats => ({
          ...prevStats,
          allEntries: [],
          latestEntry: [],
          totalDaysRecorded: prevStats.totalDaysRecorded,
          currentStreak: prevStats.currentStreak,
          longestStreak: prevStats.longestStreak,
          startYear: new Date().getFullYear(),
          startMonth: new Date().toLocaleString('en-US', { month: 'long' }),
          latestEntryDate: null,
          monthlyEntries: { ...prevStats.monthlyEntries },
      }));
  }
}, [user]);

  


/** ✅ Fetch Data on Mount */
useEffect(() => {
    fetchGratitudeData();
}, [user, fetchGratitudeData]);  


  /** ✅ Fetch Data on Mount */
/** ✅ Fetch Data on Mount */
useEffect(() => {
  fetchGratitudeData();
}, [user, fetchGratitudeData]);  

const handleSubmit = async (e) => {
  e.preventDefault();
  const nonEmptyEntries = newEntries.filter(entry => entry.trim());

  if (nonEmptyEntries.length < 3) {
      setMessage("Please write at least 3 things you are grateful for.");
      return;
  }

  if (!user || !user.id || !user.token) {
      setMessage("You must be logged in to add gratitude entries.");
      return;
  }

  setLoading(true);
  try {
      const payload = { content: nonEmptyEntries };

      await axios.post(`${API_BASE_URL}/user/${user.id}/gratitude`, payload, {
          headers: { Authorization: `Bearer ${user.token}` },
      });

      setMessage("Gratitude entry added successfully!");
      setNewEntries(["", "", "", "", ""]);
      await fetchGratitudeData();
  } catch (error) {
      console.error("❌ Error saving entry:", error.response?.data || error);
      setMessage("Error: Could not save your entry. Please try again.");
  } finally {
      setLoading(false);
  }
};






const filteredEntries = stats.allEntries?.filter(entry =>
  entry?.gratitudeSubject?.toLowerCase().includes(searchQuery.toLowerCase())
) || [];


const handleDelete = async (entryId) => {
  if (!window.confirm("Are you sure you want to delete this entry? This action cannot be undone.")) {
      return;
  }

  if (!user || !user.id || !user.token) {
      setMessage("You must be logged in to delete entries.");
      return;
  }

  setStats((prevStats) => {
      const updatedEntries = prevStats.allEntries.filter(entry => entry._id !== entryId);

      return {
          ...prevStats,
          allEntries: updatedEntries,
          latestEntry: updatedEntries
              .sort((a, b) => new Date(b.date) - new Date(a.date)) // ✅ Sort by timestamp
              .slice(0, 5), // ✅ Keep latest 5 entries
      };
  });

  try {
      await axios.delete(`${API_BASE_URL}/user/${user.id}/gratitude/${entryId}`, {
          headers: { Authorization: `Bearer ${user.token}` },
      });

      setMessage("Entry deleted successfully!");
  } catch (error) {
      console.error("❌ Error deleting entry:", error);
      setMessage("Error: Could not delete entry. Please try again.");
  }
};







const handleEdit = (entry) => {
  setEditingEntry(entry); // ✅ Store the entire entry object instead of index
};



const handleSaveEdit = async (entryId, newText) => {
  if (!user || !user.id || !user.token) {
      setMessage("You must be logged in to update entries.");
      return;
  }


  try {
       await axios.put(`${API_BASE_URL}/user/${user.id}/gratitude/${entryId}`, { 
          gratitudeSubject: newText  // ✅ Only update the specific field
      }, {
          headers: { Authorization: `Bearer ${user.token}` }
      });

      

      // ✅ Update allEntries in state
      setStats((prevStats) => ({
          ...prevStats,
          allEntries: prevStats.allEntries.map((entry) =>
              entry._id === entryId ? { ...entry, gratitudeSubject: newText } : entry
          ),
          // ✅ Also update latestEntry if the entry is found there
          latestEntry: prevStats.latestEntry.map((entry) =>
              entry._id === entryId ? { ...entry, gratitudeSubject: newText } : entry
          ),
      }));

      setMessage("Entry updated successfully!");
      setEditingEntry(null); // ✅ Exit edit mode
  } catch (error) {
      console.error("❌ Error updating entry:", error.response?.data || error);
      setMessage("Error: Could not update entry.");
  }
};


// ✅ Function to Share Gratitude Streak
const shareStreak = async () => {
  if (!navigator.share) {
    alert("⚠️ Sharing is not supported on this device.");
    return;
  }

  const { currentStreak, longestStreak, totalDaysRecorded } = stats;

  // ✅ Properly format "day" vs. "days"
  const currentDayText = currentStreak === 1 ? "day" : "days";
  const longestDayText = longestStreak === 1 ? "day" : "days";
  const totalDaysText = totalDaysRecorded === 1 ? "day" : "days";

  // ✅ Format the message with dynamic values
  const shareMessage = `🔥 I've kept my gratitude streak going for ${currentStreak} ${currentDayText}!  
📅 Total days: ${totalDaysRecorded} ${totalDaysText}  
Longest streak: ${longestStreak} ${longestDayText}.  

Join me in building a gratitude habit! ❤️☁️ #GratiSphere  

🌐 wholenessforward.com`;

  try {
    await navigator.share({
      text: shareMessage,
    });

   
  } catch (error) {
    console.error("❌ Error sharing streak:", error);
  }
};




  const renderStatistics = () => {
    const orderedMonths = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
  
    const recordedMonths = Object.entries(stats.monthlyEntries || {})
      .map(([key, count]) => {
        if (!key || typeof key !== 'string') return null; 
        if (!key.includes('-')) return null; 
  
        const [month, year] = key.split('-'); 
        const numericCount = parseInt(count || 0, 10); 
  
        if (!month || !year || isNaN(numericCount)) return null; 
  
        return { month, year, count: numericCount };
      })
      .filter(entry => entry !== null && entry.count > 0) 
      .sort((a, b) => {
        const monthIndexA = orderedMonths.indexOf(a.month);
        const monthIndexB = orderedMonths.indexOf(b.month);
        return (monthIndexA !== -1 ? monthIndexA : 99) - (monthIndexB !== -1 ? monthIndexB : 99);
      });
  
    return (
        <div className="statistics-container">
          <h3 className="grat-stats">Statistics</h3>
          <div className="stats-grid">
            <div>
              <h4>{stats.totalDaysRecorded || 0}</h4>
              <p>Total Days Recorded</p>
            </div>
            <div>
              <h4>{stats.currentStreak || 0}</h4>
              <p>Current Streak</p>
            </div>
            <div>
              <h4>{stats.longestStreak || 0}</h4>
              <p>Max Streak</p>
            </div>
          </div>
  
          {/* ✅ Show Start Year Above the First Recorded Month */}
          {recordedMonths.length > 0 && (
            <p className="start-year">
              📅 <strong>{stats.startYear}</strong>
            </p>
          )}
  
          {/* ✅ Updated Monthly Entries Bar Chart */}
          <div className="monthly-entries">
            {recordedMonths.map(({ month, count }) => (
              <div key={month} className="bar-container">
                <span className="month-label">{month.substring(0, 3)}</span>
                <div className="bar" style={{ width: `${count * 10}px` }}></div> {/* ✅ Bar is inline */}
                <span className="entry-count">({count} {count === 1 ? "day" : "days"})</span> 
              </div>
            ))}
          </div>
              {/* ✅ Share Gratitude Streak Button */}
            <div className="share-streak-container">
              <button className="button" onClick={shareStreak}>Share Streak</button>
            </div>

        </div>
    );
  };
  
  
<GratitudeWordCloud words={stats.allEntries.map(entry => entry.gratitudeSubject) || []} />


  /** ✅ Render Latest Entries */
  const renderLatestEntry = () => {
    if (!stats.latestEntry || stats.latestEntry.length === 0) {
      return (
        <div className="latest-entry-container">
          <p className="grat-class">
            Your gratitude journey continues—<br />
            What will you add today?
          </p>
        </div>
      );
    }

    // ✅ Extract latest timestamp and format date
    const latestDate = new Date(stats.latestEntry[0].date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <div className="latest-entry-container">
        <h3 className="grat-stats">Latest Entries</h3>
        <ul className="latest-entry-list">
          {stats.latestEntry.map((entry, index) => (
            <li key={entry._id || index}>
              <strong>{entry.gratitudeSubject}</strong>
              {entry.gratitudeDetails && <p className="entry-details">{entry.gratitudeDetails}</p>}
            </li>
          ))}
        </ul>
        <div className="latest-entry-date">{latestDate}</div>
      </div>
    );
  };



 


return (
  <div className="gratitude-tracker-container">
        {/* ✅ Logo at the Top */}
        <div className="logo-container">
      <img src={logoPath} alt="GratiSphere Logo" className="gratitude-logo" />
        </div>
        <div className="tracker-title2">GratiSphere</div>
    <p className="gr-instructions">  Your gratitude cloud reflects what you cherish most. 
      The more you acknowledge something, the more it grows. Start adding today and see what stands out.
</p>
    {/* ✅ Show Word Cloud Below Gratitude Stats */}
    <GratitudeWordCloud words={stats.allEntries.map(entry => entry.gratitudeSubject).filter(Boolean)} />

    <p className="instructions">   
    Studies show that gratitude enhances emotional well-being, strengthens the immune system, 
    and deepens our sense of connection and belonging. By putting it into words, you’re reshaping 
    your brain for positivity—one entry at a time.
</p>
    {/* ✅ Render Statistics (Updated Total Days) */}
    {renderStatistics()}
    <p className="instructions">Keeping a streak helps build the habit of gratitude. 
    Write at least three things each day to keep your streak alive. </p>
    <div className="micro-void"></div>
    <p className="instructions2"> {randomMessage}</p>
    <div className="micro-void"></div>
    <form onSubmit={handleSubmit} className="gratitude-form">
    <div className="entries-container">
    {newEntries.map((entry, index) => (
        <div key={index} className="entry-wrapper">
            <div className="gratitude-entry-container">
                <textarea
                    value={entry}
                    onChange={(e) => {
                        const updated = [...newEntries];
                        updated[index] = e.target.value.substring(0, 20);
                        setNewEntries(updated);
                    }}
                    className="gratitude-entry-subject"
                    rows="1"
                    maxLength="20"
                    placeholder={`What am I grateful for? ${index + 1}`}
                />
                <span className="char-count">{entry.length}/20</span>
            </div>
        </div>
    ))}
</div>

  <div className="micro-void"></div>
  {/* ✅ Improved Button UX */}
  <button type="submit" className="button-centered" disabled={loading}>
    {loading ? 'Submitting...' : 'Submit'}
  </button>

  {/* ✅ Show Loading State */}
  {loading && <p className="loading-message">Processing your gratitude entry...</p>}
</form>


    {/* ✅ Display success or error messages */}
    {message && <p className="message">{message}</p>}

  {/* ✅ Display the latest entry */}
  {renderLatestEntry()}

{/* ✅ Search Bar for Past Entries */}
<div className="search-container">
  <input
    type="text"
    placeholder="Search to edit or delete entries..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    className="search-bar"
  />

{/* ✅ Show Search Results if There Are Matches */}
{searchQuery.trim() !== "" && (
  <div className="search-results-container">
    {filteredEntries.length > 0 ? (
      <ul className="search-dropdown">
        {filteredEntries.map((entry) => (
          <li key={entry._id} className="search-result">
            {editingEntry && editingEntry._id === entry._id ? (
              <input
                type="text"
                value={editingEntry.gratitudeSubject}
                onChange={(e) =>
                  setEditingEntry({ ...editingEntry, gratitudeSubject: e.target.value })
                }
                className="edit-input"
              />
            ) : (
              <span>{entry.gratitudeSubject}</span>
            )}

            <div className="entry-actions">
              {editingEntry && editingEntry._id === entry._id ? (
                <>
                  <button onClick={() => handleSaveEdit(entry._id, editingEntry.gratitudeSubject)}>
                    💾 Save
                  </button>
                  <button onClick={() => setEditingEntry(null)}>❌ Cancel</button>
                </>
              ) : (
                <>
                  <button onClick={() => handleEdit(entry)}>✏️ Edit</button>
                  <button onClick={() => handleDelete(entry._id)}>🗑️ Delete</button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <p className="no-results">No results found.</p>
    )}
  </div>
)}
</div>
  </div>
);
};




export default GratitudeTracker;
